import { Component, Input } from '@angular/core';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { Icons, FeatureFlagService } from '@lsq-frontend/common-ui';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { PopoverPlacement } from 'ngx-smart-popover';
import { Color } from '@lsq-frontend/common-ui';
/**
 * Reusable styling for cluster of information that is repeated on the Dashboard homepage
 * @export
 * @class DashboardAmountClusterComponent
 * @property {number} value The featured value
 * @property {string} tooltipText Body of the tooltip
 * @property {string} tooltipOrientation Optional override of the tooltip orientation
 * @property {string} label The label above the dollar amount
 * @property {string} linkDestination Destination of the link (optional)
 * @property {string} queryParams Query param for the destination of the link (optional)
 * @property {boolean} useCurrencyPipe Apply the currency pipe to the value
 * @property {boolean} usePercentPipe Apply the percent pipe to the value
 */
@Component({
    selector: 'dashboard-amount-cluster',
    template: `
        <div class="top-row">
            <ng-container *claim="claim.ViewReports; missing: viewReportsForbidden">
                <ng-container *ngIf="linkDestination">
                    <lsq-button appearance="transparent" [tabindex]="'-1'">
                        <a
                            *ngIf="isInteger(label)"
                            [queryParams]="queryParams"
                            [routerLink]="linkDestination"
                        >
                            {{ label | number }}</a
                        >
                        <a
                            *ngIf="!isInteger(label)"
                            [queryParams]="queryParams"
                            [routerLink]="linkDestination"
                        >
                            {{ label }}</a
                        >
                    </lsq-button>
                </ng-container>
                <ng-container *ngIf="!linkDestination">
                    <span class="small-uppercase" [attr.aria-hidden]="true">{{ label }}</span>
                </ng-container>
            </ng-container>

            <ng-template #viewReportsForbidden>
                <span class="small-uppercase" [attr.aria-hidden]="true">{{ label }}</span>
            </ng-template>

            <span class="sr-only">{{ label }}</span>
            <div>
                <lsq-icon
                    *ngIf="!!tooltipText"
                    [attr.tabindex]="0"
                    [type]="icons.QuestionCircleLight"
                    [popoverOnHover]="true"
                    [height]="14"
                    [width]="14"
                    [popover]="tooltipText"
                    [placement]="tooltipPlacement"
                    [popoverPlacement]="tooltipPlacement"
                    [color]="colors.GrayDark"
                >
                </lsq-icon>
            </div>
        </div>
        <div
            class="dashboard-value"
            [style.margin-right]="!!tooltipText ? '2.2rem' : '0'"
            [style.font-weight]="boldedAmount ? 'bold' : 'normal'"
        >
            <ng-container *ngIf="useCurrencyPipe">
                {{ value || 0 | lsqCurrency }}
            </ng-container>
            <ng-container *ngIf="usePercentPipe && !useCurrencyPipe">
                {{ value || 0 | percent }}
            </ng-container>
            <ng-container *ngIf="!usePercentPipe && !useCurrencyPipe">
                {{ value || 0 }}
            </ng-container>
        </div>
    `,
    styleUrls: ['./dashboard-amount-cluster.component.scss']
})
export class DashboardAmountClusterComponent {
    @Input() public value: number;
    @Input() public tooltipText: string;
    @Input() public tooltipPlacement = PopoverPlacement.Top;
    @Input() public label?: string;
    @Input() public linkDestination?: string;
    @Input() public queryParams?: object;
    @Input() public useCurrencyPipe = false;
    @Input() public usePercentPipe = false;
    @Input() public boldedAmount = false;
    public claim = DashboardClaim;
    public icons = Icons;
    public colors = Color;

    public isInteger(value: string): boolean {
        return Number.isInteger(+value);
    }

    constructor(private _featureFlagsService: FeatureFlagService) {}
}
